import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import {
  CheckCircleOutline as YesIcon,
  HighlightOff as NoIcon,
  HelpOutline as DontKnowIcon,
  Lightbulb as WhyIcon,
  Visibility as WhatIcon,
  Apartment as PlaceholderIcon
} from '@mui/icons-material';
import { localized, ANSWER_LABELS } from '../../data/questionnaireContent';

// Couleurs des 3 réponses (mêmes repères visuels partout)
const ANSWER_STYLES = {
  oui: { color: '#27AE60', icon: <YesIcon /> },
  non: { color: '#7F8C8D', icon: <NoIcon /> },
  je_ne_sais_pas: { color: '#E67E22', icon: <DontKnowIcon /> }
};

function QuestionCard({ question, answer, onAnswer, lang }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgSrc = question.image
    ? `${process.env.PUBLIC_URL}/assets/questionnaire/${question.image}`
    : null;

  // Précharge l'image : on n'affiche la photo que si elle existe réellement,
  // sinon on garde l'illustration de repli (pas d'image cassée).
  useEffect(() => {
    setImgLoaded(false);
    if (!imgSrc) return;
    const img = new Image();
    img.onload = () => setImgLoaded(true);
    img.src = imgSrc;
  }, [imgSrc]);

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', width: '100%' }}>
      {/* Illustration */}
      <Box
        sx={{
          height: 200,
          borderRadius: 3,
          mb: 3,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
        }}
      >
        {imgLoaded ? (
          <Box component="img" src={imgSrc} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <PlaceholderIcon sx={{ fontSize: 72, color: 'primary.light', opacity: 0.5 }} />
        )}
      </Box>

      {/* Question */}
      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center', mb: 3, lineHeight: 1.35 }}>
        {localized(question.question, lang)}
      </Typography>

      {/* Boutons de réponse */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {['oui', 'non', 'je_ne_sais_pas'].map((value) => {
          const selected = answer === value;
          const style = ANSWER_STYLES[value];
          return (
            <Button
              key={value}
              fullWidth
              size="large"
              startIcon={style.icon}
              onClick={() => onAnswer(value)}
              sx={{
                justifyContent: 'flex-start',
                py: 1.8,
                px: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                border: '2px solid',
                borderColor: selected ? style.color : 'divider',
                backgroundColor: selected ? `${style.color}14` : 'transparent',
                color: selected ? style.color : 'text.primary',
                '&:hover': {
                  borderColor: style.color,
                  backgroundColor: `${style.color}0A`,
                },
              }}
            >
              {localized(ANSWER_LABELS[value], lang)}
            </Button>
          );
        })}
      </Stack>

      {/* Aide didactique */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.015)' }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: question.what ? 2 : 0 }}>
          <WhyIcon sx={{ color: '#F39C12', mt: 0.3 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {localized(UI_WHY, lang)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {localized(question.why, lang)}
            </Typography>
          </Box>
        </Box>
        {question.what && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <WhatIcon sx={{ color: '#3498DB', mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {localized(UI_WHAT, lang)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {localized(question.what, lang)}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// Titres d'aide (importés depuis UI mais isolés ici pour lisibilité)
const UI_WHY = { fr: "Pourquoi c'est important", en: 'Why it matters', pt: 'Porque é importante', mg: 'Nahoana no zava-dehibe' };
const UI_WHAT = { fr: 'Quoi regarder', en: 'What to look at', pt: 'O que observar', mg: 'Inona no jerena' };

export default QuestionCard;
